/* eslint-disable react-hooks/exhaustive-deps */
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Box,
  Checkbox,
  Flex,
  Icon,
  SearchInput,
  Space,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { CloseSIcon } from '@tonic-one/react-icons';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DelModal from 'src/pages/Report/ActionModal/del';

import { GlobalApi } from '@Apis';
import ErrorAlert from '../../../components/Common/ErrorAlert';
import { useDisclosure } from '../../../components/Common/Tooltip/TextEllipsis/utils';
import CSpinner from '../../../components/CSpinner';
import IconButton from '../../../components/IconButton';
import config from '../../../config';
import { PLMExceptionProps } from '../../../types/globalSetting';
import LoadingBtn from '../../Report/LoadingBtn';
import SmartTooltip from '../../Report/SmartTooltip';
import { getUtcTime } from '../../../utils/time';
import { useUtcOffsetStore } from '../../../models/useUtcOffsetModel';
import { usePermissionStore } from '@Models/usePermissionModel';
import { PermissionNameEnum } from '@Types/permissions';

const PMLExceptionList = () => {
  const { widthEditablePermission } = usePermissionStore();
  const withEditPermission = widthEditablePermission(
    PermissionNameEnum.administrator
  );
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);
  const { t } = useTranslation();
  const [items, setItems] = useState<PLMExceptionProps[] | []>([]);
  const [allItems, setAllItems] = useState<PLMExceptionProps[] | []>([]);
  const [delModalTitle, setDelModalTitle] = useState<string>();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [delModalContent, setDelModalContent] = useState<
    JSX.Element[] | undefined
  >(undefined);
  const { offset } = useUtcOffsetStore();
  const [delLoading, setDelLoading] = useState(false);
  const search = (keyword: string) => {
    if (keyword !== '') {
      const temp = allItems.filter(
        (item) => item.sha1.toLowerCase().indexOf(keyword.toLowerCase()) > -1
      );
      setItems(temp);
    } else {
      setItems(allItems);
    }
  };
  const hoverBackgroundColor = (
    {
      dark: 'rgba(255, 255, 255, 0.12)',
      light: 'rgba(0, 0, 0, 0.12)',
    } as any
  )[colorMode];

  async function loadData() {
    try {
      setLoading(true);
      const data: PLMExceptionProps[] = (await GlobalApi.getPLMExceptionList())
        .data;
      setItems(data);
      setAllItems(data);
    } catch (error: any) {
      setErrorMsg(t('common.apiException.internalError'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    (async function doCall() {
      await loadData();
    })();
  }, []);

  const columns: ColumnDef<PLMExceptionProps>[] = useMemo(
    () => [
      {
        id: 'selection',
        header: ({ table }) => (
          <Checkbox
            checked={table.getIsAllRowsSelected()}
            indeterminate={table.getIsSomeRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            indeterminate={row.getIsSomeSelected()}
            onChange={row.getToggleSelectedHandler()}
          />
        ),
        size: 48,
      },
      {
        id: 'sha1',
        header: t('globalSetting.PMLExceptions.column.sha1'),
        accessorKey: 'sha1',
        size: 400,
      },
      {
        id: 'fileName',
        header: t('globalSetting.PMLExceptions.column.fileName'),
        accessorKey: 'fileName',
        size: 400,
        style: {
          textAlign: 'right',
        },
      },
      {
        id: 'createdTime',
        header: t('globalSetting.PMLExceptions.column.createdTime'),
        cell: ({ row }) => {
          const { createdTime } = row.original;
          return getUtcTime(createdTime, offset, t('common.momentDateFormat'));
        },
        size: 200,
        style: {
          textAlign: 'right',
        },
      },
    ],
    []
  );
  const [rowSelection, setRowSelection] = useState({});
  const table = useReactTable({
    data: items,
    columns,
    defaultColumn: {
      minSize: 40,
    },
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.sha1,
    state: {
      rowSelection,
    },
    enableRowSelection: true, // enable row selection for all rows
    // enableRowSelection: row => row.original.detections > 0, // or enable row selection conditionally per row
    onRowSelectionChange: setRowSelection,
  });
  const cellHeight = 36;
  const headerHeight = 36;
  const rowsToDisplay = 5;
  const tableStyleProps = {
    height: headerHeight + rowsToDisplay * cellHeight,
    // Uncomment the following line to specify the width and enable horizontal scrollbar
    // width: 200,
  };
  const selectedRowCount = Object.keys(rowSelection).length;
  const handleDel = () => {
    setDelModalTitle(t('globalSetting.PMLExceptions.actions.delete.title'));
    const selectedRows = getSelectedRows();
    const content = selectedRows.map((item) => (
      <Text key={item.sha1} mb={8}>
        {item.sha1}
      </Text>
    ));
    setDelModalContent(content);
    onOpen();
  };
  const reset = () => {
    setRowSelection({});
    search('');
  };

  function getSelectedRows() {
    const selectedRows: PLMExceptionProps[] = [];
    for (const key of Object.keys(rowSelection)) {
      const item = items.find((item) => item.sha1 === key);
      if (item) {
        selectedRows.push(item);
      }
    }
    return selectedRows;
  }

  const confirmDel = async () => {
    setDelLoading(true);
    if (!selectedRowCount) return;
    try {
      const selectedRows = getSelectedRows();
      const res = await GlobalApi.deletePLMExceptionList(selectedRows);
      if (res.data?.deleteCount) {
        reset();
        window.showNotification({
          titleMsg: t('globalSetting.PMLExceptions.delete.success'),
          severity: 'success',
        });
      }
    } catch (error: any) {
      window.showNotification({
        titleMsg: t('common.apiException.internalError'),
        severity: 'error',
      });
    } finally {
      onClose();
      setDelLoading(false);
      await loadData();
    }
  };
  return (
    <Box px="4x">
      {loading && <CSpinner />}
      <Box>
        <DelModal
          visible={isOpen}
          onClose={onClose}
          onConfirm={confirmDel}
          loading={delLoading}
          title={delModalTitle}
          content={delModalContent}
          withEditPermission={withEditPermission}
        ></DelModal>
        {errorMsg && (
          <ErrorAlert
            errorMsg={errorMsg}
            onClose={() => setErrorMsg(undefined)}
          />
        )}
        <Flex m="16px 0px">
          <Text fontSize="lg" lineHeight="lg" color={colorStyle.color.primary}>
            {t('globalSetting.PMLExceptions.tab.title')}
          </Text>
        </Flex>
        <Flex m="16px 0px">
          <Text
            color={colorStyle.color.secondary}
            lineHeight="lg"
            maxWidth={1000}
          >
            {t('globalSetting.PMLExceptions.description', {
              count:
                config.ui.globalSettings.limitation
                  .predictiveMachineLearningMaxCount,
            })}
          </Text>
        </Flex>
        <Flex m="16px 0px">
          <Text>
            <Space>{t('globalSetting.note.title')}</Space>
            <Space width="1x"></Space>
            <Space color={colorStyle.color.secondary}>
              {t('globalSetting.note.applyToAllOrganizations')}
            </Space>
          </Text>
        </Flex>
        <Flex alignItems="center">
          {selectedRowCount !== 0 ? (
            <Box mr={16}>
              <Stack direction="row">
                <LoadingBtn
                  variant="secondary"
                  mr="4x"
                  onClick={handleDel}
                  disabled={!withEditPermission}
                >
                  {t('reportCommon.buttons.delete')}
                </LoadingBtn>
                <IconButton onClick={reset}>
                  <Icon as={CloseSIcon}></Icon>
                </IconButton>
                <Box lineHeight="2rem">
                  <Text color={colorStyle.color.tertiary}>
                    {selectedRowCount}&nbsp;
                    {t('common.label.selected')}
                  </Text>
                </Box>
              </Stack>
            </Box>
          ) : null}
          {selectedRowCount === 0 ? (
            <Stack direction="row">
              <Space width="12px" />
              <SearchInput
                placeholder={t('globalSetting.PMLExceptions.column.sha1')}
                onChange={(e: any) => {
                  search(e.target.value);
                }}
                onClearInput={() => {
                  search('');
                }}
                width="374px"
              />
            </Stack>
          ) : null}
        </Flex>
        <Table mt={28} layout="flexbox" {...tableStyleProps}>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableHeaderRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const styleProps = {
                    minWidth: header.column.columnDef.minSize,
                    width: header.getSize(),
                  };
                  return (
                    <TableHeaderCell key={header.id} {...styleProps}>
                      {header.isPlaceholder ? null : (
                        <SmartTooltip>
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </SmartTooltip>
                      )}
                    </TableHeaderCell>
                  );
                })}
              </TableHeaderRow>
            ))}
          </TableHeader>
          <TableBody overflowY="scroll">
            {table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                _hover={{
                  backgroundColor: hoverBackgroundColor,
                }}
              >
                {row.getVisibleCells().map((cell) => {
                  const styleProps = {
                    minWidth: cell.column.columnDef.minSize,
                    width: cell.column.getSize(),
                  };
                  return (
                    <TableCell key={cell.id} {...styleProps}>
                      <SmartTooltip>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </SmartTooltip>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};
export default PMLExceptionList;
